import React, { useState, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { Activity, Lock, Mail, Eye, EyeOff, User } from "lucide-react";

// Purple theme tokens matching Layout/Dashboard
const tokens = {
  primary: "#7c3aed",
  primaryLight: "#ede9fe",
  primaryDark: "#5b21b6",
  accent: "#a78bfa",
  background: "#1e1b4b",
  surface: "#ffffff",
  error: "#ef4444",
  text: {
    primary: "#1e1b4b",
    secondary: "#6b7280",
    inverse: "#ffffff",
  },
};

// Password strength helpers
function getStrength(pwd: string): {
  score: number;
  label: string;
  color: string;
} {
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  if (score <= 1) return { score, label: "Très faible", color: "#ef4444" };
  if (score === 2) return { score, label: "Faible", color: "#f97316" };
  if (score === 3) return { score, label: "Moyen", color: "#f59e0b" };
  if (score === 4) return { score, label: "Fort", color: "#22c55e" };
  return { score, label: "Très fort", color: "#10b981" };
}

function InputField({
  id,
  label,
  type,
  value,
  onChange,
  placeholder,
  icon,
  autoComplete,
  error,
  required = true,
  rightSlot,
}: {
  id: string;
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  icon: React.ReactNode;
  autoComplete: string;
  error?: string;
  required?: boolean;
  rightSlot?: React.ReactNode;
}) {
  const [focused, setFocused] = React.useState(false);
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[13px] font-medium mb-1.5"
        style={{ color: tokens.text.primary }}
      >
        {label}
        {required && (
          <span
            className="ml-0.5"
            style={{ color: tokens.error }}
            aria-hidden="true"
          >
            *
          </span>
        )}
      </label>
      <div className="relative h-12 flex items-center">
        <span
          className="absolute left-3 pointer-events-none"
          style={{ color: focused ? tokens.primary : "#9ca3af" }}
        >
          {icon}
        </span>
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-required={required}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          className="w-full h-full rounded-lg text-[14px] transition-all duration-200 outline-none"
          style={{
            paddingLeft: "2.5rem",
            paddingRight: rightSlot ? "3rem" : "0.75rem",
            border: `1px solid ${error ? tokens.error : focused ? tokens.primary : "#e5e7eb"}`,
            boxShadow:
              focused && !error
                ? `0 0 0 3px ${tokens.primary}18`
                : error
                  ? `0 0 0 3px ${tokens.error}18`
                  : "none",
            backgroundColor: "#ffffff",
            color: tokens.text.primary,
          }}
        />
        {rightSlot && <div className="absolute right-0">{rightSlot}</div>}
      </div>
      {error && (
        <p
          id={`${id}-error`}
          className="mt-1 text-[12px]"
          style={{ color: tokens.error }}
          role="alert"
          aria-live="polite"
        >
          {error}
        </p>
      )}
    </div>
  );
}

export default function Login() {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { login, register, loading } = useAuth();

  const strength = useMemo(() => getStrength(password), [password]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (mode === "register" && !name.trim()) errs.name = "Le nom est requis";
    if (!email.trim()) errs.email = "L'email est requis";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      errs.email = "Email invalide";
    if (!password) errs.password = "Le mot de passe est requis";
    else if (mode === "register" && password.length < 8)
      errs.password = "Minimum 8 caractères";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const switchMode = () => {
    setMode((m) => (m === "login" ? "register" : "login"));
    setGlobalError("");
    setFieldErrors({});
    setName("");
    setEmail("");
    setPassword("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGlobalError("");
    if (!validate()) return;

    const result =
      mode === "login"
        ? await login(email, password)
        : await register(name, email, password);

    if (!result.success) {
      setGlobalError(result.error || "Une erreur est survenue");
    }
  };

  const eyeButton = (
    <button
      type="button"
      onClick={() => setShowPassword((p) => !p)}
      className="flex items-center justify-center w-11 h-11 rounded-r-lg transition-colors"
      style={{
        color: "#9ca3af",
        background: "transparent",
        border: "none",
        cursor: "pointer",
      }}
      aria-label={
        showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"
      }
    >
      {showPassword ? (
        <EyeOff className="w-4 h-4" />
      ) : (
        <Eye className="w-4 h-4" />
      )}
    </button>
  );

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background:
          "linear-gradient(135deg, #1e1b4b 0%, #4c1d95 50%, #1e1b4b 100%)",
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div
          className="p-8 text-center"
          style={{
            background: `linear-gradient(135deg, ${tokens.primary} 0%, ${tokens.accent} 100%)`,
          }}
        >
          <div
            className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
          >
            <Activity className="w-8 h-8 text-white" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold text-white">API Monitor</h1>
          <p
            className="mt-1.5 text-[13px]"
            style={{ color: "rgba(255,255,255,0.8)" }}
          >
            {mode === "login"
              ? "Connectez-vous à votre espace"
              : "Créez votre compte"}
          </p>
        </div>

        {/* Form */}
        <div className="p-8">
          {/* Mode toggle tabs */}
          <div
            className="flex rounded-xl mb-6 p-1"
            style={{ backgroundColor: "#f3f4f6", display: "none" }}
          >
            {(["login", "register"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  if (m !== mode) switchMode();
                }}
                className="flex-1 py-2 rounded-lg text-[13px] font-medium transition-all duration-200"
                style={{
                  background: mode === m ? "#ffffff" : "transparent",
                  color: mode === m ? tokens.primary : "#6b7280",
                  boxShadow: mode === m ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {m === "login" ? "Connexion" : "Inscription"}
              </button>
            ))}
          </div>

          {/* Global error */}
          {globalError && (
            <div
              className="mb-4 p-3 rounded-lg text-[13px] flex items-start gap-2"
              style={{
                backgroundColor: "rgba(239,68,68,0.08)",
                border: `1px solid ${tokens.error}`,
                color: tokens.error,
              }}
              role="alert"
              aria-live="assertive"
            >
              <Lock className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
              {globalError}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            {mode === "register" && (
              <InputField
                id="name"
                label="Nom complet"
                type="text"
                value={name}
                onChange={(v) => {
                  setName(v);
                  setFieldErrors((e) => ({ ...e, name: "" }));
                }}
                placeholder="Jean Dupont"
                icon={<User className="w-4 h-4" />}
                autoComplete="name"
                error={fieldErrors.name}
              />
            )}

            <InputField
              id="email"
              label="Email"
              type="email"
              value={email}
              onChange={(v) => {
                setEmail(v);
                setFieldErrors((e) => ({ ...e, email: "" }));
              }}
              placeholder="vous@exemple.com"
              icon={<Mail className="w-4 h-4" />}
              autoComplete="email"
              error={fieldErrors.email}
            />

            <div>
              <InputField
                id="password"
                label="Mot de passe"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(v) => {
                  setPassword(v);
                  setFieldErrors((e) => ({ ...e, password: "" }));
                }}
                placeholder="••••••••"
                icon={<Lock className="w-4 h-4" />}
                autoComplete={
                  mode === "register" ? "new-password" : "current-password"
                }
                error={fieldErrors.password}
                rightSlot={eyeButton}
              />
              {/* Password strength bar — only on register */}
              {mode === "register" && password.length > 0 && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className="flex-1 h-1 rounded-full transition-all duration-300"
                        style={{
                          backgroundColor:
                            i <= strength.score ? strength.color : "#e5e7eb",
                        }}
                      />
                    ))}
                  </div>
                  <p className="text-[11px]" style={{ color: strength.color }}>
                    {strength.label}
                  </p>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl font-semibold text-[14px] transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{
                minHeight: "48px",
                background: loading
                  ? "#a78bfa"
                  : `linear-gradient(135deg, ${tokens.primary} 0%, ${tokens.accent} 100%)`,
                color: "#ffffff",
                border: "none",
                boxShadow: loading
                  ? "none"
                  : "0 4px 14px rgba(124,58,237,0.35)",
              }}
              onMouseEnter={(e) => {
                if (!loading)
                  e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
              }}
              aria-busy={loading}
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {mode === "login" ? "Connexion…" : "Inscription…"}
                </>
              ) : mode === "login" ? (
                "Se connecter"
              ) : (
                "Créer mon compte"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
